import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { ConsentBanner } from "@/components/site/ConsentBanner";
import { useEffect, useRef } from "react";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Portfolio from "@/pages/Portfolio";
import ServicesHub from "@/pages/ServicesHub";
import ServiceDetail from "@/pages/ServiceDetail";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Contact from "@/pages/Contact";
import Process from "@/pages/Process";
import Equipment from "@/pages/Equipment";
import Industries from "@/pages/Industries";
import CaseStudies from "@/pages/CaseStudies";
import Crew from "@/pages/Crew";
import Press from "@/pages/Press";
import Careers from "@/pages/Careers";
import Pricing from "@/pages/Pricing";
import References from "@/pages/References";
import HotelsLanding from "@/pages/HotelsLanding";
import AgenciesLanding from "@/pages/AgenciesLanding";
import MedicalLanding from "@/pages/MedicalLanding";
import ConstructionLanding from "@/pages/ConstructionLanding";
import PhotoGallery from "@/pages/PhotoGallery";
import ThankYou from "@/pages/ThankYou";
import Privacy from "@/pages/Privacy";
import WorkingAgreement from "@/pages/WorkingAgreement";
import NotFound from "@/pages/NotFound";
import { SLUG_REDIRECTS } from "@/lib/siteData";
import { resolveWixRedirect } from "@/lib/wixRedirects";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" }); }, [pathname]);
  return null;
};

// Fires a GA4 page_view on every client-side route change AFTER the initial
// mount. The initial page_view is already fired by gtag('config', ...) in
// public/index.html — skipping the first render here avoids double-counting.
const GAPageView = () => {
  const location = useLocation();
  const isFirst = useRef(true);
  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    window.gtag("event", "page_view", {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
      send_to: "G-73MKGWZJVV",
    });
  }, [location.pathname, location.search]);
  return null;
};

const RedirectFromOldSlugs = () => {
  const { pathname } = useLocation();
  // Fast lookup first (old Wix bare slugs already listed in SLUG_REDIRECTS)
  const explicit = SLUG_REDIRECTS[pathname];
  if (explicit && explicit !== pathname) return <Navigate to={explicit} replace />;
  // Broader Wix catch-all: /service-page/* and any legacy path we know about.
  const wix = resolveWixRedirect(pathname);
  if (wix && wix !== pathname) return <Navigate to={wix} replace />;
  return null;
};

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <GAPageView />
          <RedirectFromOldSlugs />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/services" element={<ServicesHub />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/post/:slug" element={<BlogPost />} />
            <Route path="/process" element={<Process />} />
            <Route path="/equipment" element={<Equipment />} />
            <Route path="/industries" element={<Industries />} />
            <Route path="/case-studies" element={<CaseStudies />} />
            <Route path="/crew" element={<Crew />} />
            <Route path="/press" element={<Press />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/thank-you" element={<ThankYou />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/references" element={<References />} />
            <Route path="/photography" element={<PhotoGallery />} />
            <Route path="/photos" element={<Navigate to="/photography" replace />} />
            <Route path="/photo-portfolio" element={<Navigate to="/photography" replace />} />
            <Route path="/photographer-cape-town" element={<Navigate to="/photography" replace />} />
            <Route path="/photographer-cape-town-portfolio" element={<Navigate to="/photography" replace />} />
            <Route path="/for/hotels-and-lodges" element={<HotelsLanding />} />
            <Route path="/for/marketing-agencies" element={<AgenciesLanding />} />
            <Route path="/for/medical-dental-pharma" element={<MedicalLanding />} />
            <Route path="/for/construction-services" element={<ConstructionLanding />} />
            <Route path="/for/agencies" element={<Navigate to="/for/marketing-agencies" replace />} />
            <Route path="/for/marketing" element={<Navigate to="/for/marketing-agencies" replace />} />
            <Route path="/for/medical" element={<Navigate to="/for/medical-dental-pharma" replace />} />
            <Route path="/for/dental" element={<Navigate to="/for/medical-dental-pharma" replace />} />
            <Route path="/for/pharma" element={<Navigate to="/for/medical-dental-pharma" replace />} />
            <Route path="/for/healthcare" element={<Navigate to="/for/medical-dental-pharma" replace />} />
            <Route path="/for/construction" element={<Navigate to="/for/construction-services" replace />} />
            <Route path="/for/trades" element={<Navigate to="/for/construction-services" replace />} />
            <Route path="/for/builders" element={<Navigate to="/for/construction-services" replace />} />
            <Route path="/for/developers" element={<Navigate to="/for/construction-services" replace />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
            <Route path="/working-agreement" element={<WorkingAgreement />} />
            <Route path="/terms" element={<Navigate to="/working-agreement" replace />} />
            <Route path="/terms-of-service" element={<Navigate to="/working-agreement" replace />} />
            <Route path="/for/hotels" element={<Navigate to="/for/hotels-and-lodges" replace />} />
            <Route path="/for/lodges" element={<Navigate to="/for/hotels-and-lodges" replace />} />
            <Route path="/hospitality" element={<Navigate to="/for/hotels-and-lodges" replace />} />
            <Route path="/luxury-hospitality-visibility-engine" element={<Navigate to="/for/hotels-and-lodges" replace />} />
            <Route path="/testimonials" element={<Navigate to="/references" replace />} />
            <Route path="/videographer-cape-town-prices" element={<Navigate to="/pricing" replace />} />
            <Route path="/plans-and-pricing" element={<Navigate to="/pricing" replace />} />
            {/* Preserved Wix slugs - client-side redirects */}
            <Route path="/videographer-services-cape-town" element={<Navigate to="/services" replace />} />
            <Route path="/videographer-cape-town-portfolio" element={<Navigate to="/portfolio" replace />} />
            <Route path="/contact-us-pie-in-the-sky-productions" element={<Navigate to="/contact" replace />} />
            <Route path="/free-consultation" element={<Navigate to="/contact" replace />} />
            <Route path="/videographer-cape-town" element={<Navigate to="/services/videographer-cape-town" replace />} />
            <Route path="/cape-town-video-production-company-portfolio" element={<Navigate to="/portfolio" replace />} />
            <Route path="/booking-calendar/free-consultation" element={<Navigate to="/contact" replace />} />
            <Route path="/booking-calendar/pre-production" element={<Navigate to="/services" replace />} />
            <Route path="/booking-calendar/video-production" element={<Navigate to="/services/video-production-company-cape-town" replace />} />
            <Route path="/booking-calendar/photography" element={<Navigate to="/services/photography" replace />} />
            <Route path="/booking-calendar/post-production" element={<Navigate to="/services/post-production" replace />} />
            <Route path="/booking-calendar/vehicle-rental-1" element={<Navigate to="/equipment" replace />} />
            <Route path="/booking-calendar/hair-makeup" element={<Navigate to="/contact" replace />} />
            {/* NEW: Missing redirects identified in SEO audit (2026-06) */}
            <Route path="/photographer-cape-town-portfolio" element={<Navigate to="/portfolio" replace />} />
            <Route path="/tools-tips" element={<Navigate to="/blog" replace />} />
            {/* Defensive aliases / common variations */}
            <Route path="/services/videographer" element={<Navigate to="/services/videographer-cape-town" replace />} />
            <Route path="/services/video-production" element={<Navigate to="/services/video-production-company-cape-town" replace />} />
            <Route path="/services/documentary" element={<Navigate to="/services/documentary-filmmaking" replace />} />
            <Route path="/services/ai" element={<Navigate to="/services/ai-video-editing" replace />} />
            <Route path="/services/talent" element={<Navigate to="/services/models-talent" replace />} />
            <Route path="/services/crew" element={<Navigate to="/services/production-crew-support" replace />} />
            <Route path="/services/hair-makeup" element={<Navigate to="/services/production-crew-support" replace />} />
            <Route path="/services/art-direction" element={<Navigate to="/services/production-crew-support" replace />} />
            <Route path="/case-study/:slug" element={<Navigate to="/case-studies" replace />} />
            <Route path="/blog/:slug" element={<Navigate to="/blog" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <ConsentBanner />
        </BrowserRouter>
        <Toaster theme="dark" position="bottom-right" richColors closeButton />
      </div>
    </HelmetProvider>
  );
}

export default App;
